
import { Component, Prop, Ref, Vue, Watch } from "vue-property-decorator"
import { namespace, Getter, Action, State } from "vuex-class"

import _Quill, { RangeStatic, Sources } from "quill"

import Guid from "@/scripts/guid"

import HyperlinkModal from "@/components/Controls/HyperlinkModal.vue"
import SearchDropDown from "@/components/Controls/SearchDropDown.vue"
import { AcceptedImageTypes } from "@/types/files"
import { TenantFeatures } from "@/types/tenantFeatures"

const setup = namespace("setup")

const Quill = window.Quill || _Quill

const Link = Quill.import("formats/link")

type Hyperlink = {
	text: string,
	url: string,
	replaceExistingText: boolean
}

@Component({
	components: {
		"hyperlink-modal": HyperlinkModal,
		"search-dropdown": SearchDropDown
	}
})
export default class RichTextEditor extends Vue {
	@Ref()
	editorContainer!: HTMLElement

	@Ref()
	editorParent!: HTMLElement

	@Ref()
	fileInput!: HTMLInputElement

	/**
	 * Value for v-model support
	 */
	@Prop({ type: String, default: "" })
	public value!: string;

	@Prop({ type: Boolean, default: false })
	public readonly!: boolean;

	@Prop({ default: () => null })
	public placeholder!: string | null

	@Prop({ default: null })
	public minHeight!: string | null

	@Prop({ type: Boolean, default: false })
	public userHistoryOnly!: boolean

	@Prop({ type: Boolean, default: false })
	public applyMaxHeight!: boolean

	@Prop({ type: Array, default: () => null })
	public placeholders!: { id: string, label: string }[] | null

	@Prop({ type: Boolean, default: true })
	public allowImages!: boolean

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	private editor: _Quill | null = null
	private focusWhenReady: boolean = false

	private controlId = Guid.newGuid()
	private selectedText: string = ""

	private editorRangeIndex: number = 0
	private editorRangeLength: number = 0

	private isLinkButtonActive: boolean = false
	private hyperlinkModalIsOpen: boolean = false

	private currentTextValue: string = ""
	acceptedImageTypes = AcceptedImageTypes

	get ToolBarId (): string {
		return `toolbar-${this.controlId}`
	}

	get quillFormats (): string[] {
		let formats = ["bold", "italic",
			"link", "indent",
			"header", "list",
			"underline", "strike",
			"placeholder"]

		if (this.richTextImagesEnabled) {
			formats.push("image")
		}
		return formats
	}

	@Watch("value")
	onValueChanged (value: string) {
		if (!this.editor) {
			return
		}
		const editorValue = this.getEditorValue()
		if (editorValue !== value && this.currentTextValue !== value) {
			this.editor.pasteHTML(this.value)
		}
	}

	async mounted () {
		if (this.$refs.editorContainer) {
			const optionalModules: any = {}

			this.editor = new Quill(this.editorContainer, {
				placeholder: this.placeholder || "",
				readOnly: this.readonly,
				modules: {
					toolbar: `#${this.ToolBarId}`,
					magicUrl: true,
					history: {
						userOnly: this.userHistoryOnly
					},
					...optionalModules
				},
				formats: this.quillFormats,
				scrollingContainer: this.editorParent
			})

			this.editor!.on("text-change", () => {
				const newValue = this.getEditorValue()
				if (newValue !== this.value) {
					this.currentTextValue = newValue
					this.$emit("input", newValue)
				}
			})

			this.editor!.on("selection-change", (range, oldRange, source) => {
				this.handleEditorSelectionChange(range, source)
			})

			// listen for a hyperlink click
			this.editor!.root.addEventListener("click", (ev) => {
				const range = this.editor!.getSelection()
				if (range == null) return

				// only proceed if the click is a single click (i.e., no text selection)
				if (range.length === 0) {
					// find link element using range
					const [link, offset] = (this.editor!.scroll as any).descendant(
						Link,
						range.index
					)
					// navigate to the URl in a new tab if the dom node is of type link
					if (link && link.domNode && link.domNode.href) {
						window.open(link.domNode.href, "_blank", "noopener,noreferrer")
					}
				}
			})

			if (this.value) {
				this.editor!.pasteHTML(this.value)
			}
		}
	}

	beforeDestroy () {
		if (this.editor) {
			this.editor = null
		}
	}

	@Watch("readonly")
	onReadonlyChanged (readonly: boolean) {
		if (this.editor) {
			this.editor.enable(!readonly)
			if (this.focusWhenReady && !readonly) {
				this.focusWhenReady = false
				this.editor.focus()
			}
		}
	}

	focusOut (event: FocusEvent) {
		// Ensure that this event isn't triggered when changing between children, eg clicking a button while focused on text
		if (!event.relatedTarget || !this.$el.contains(event.relatedTarget as any)) {
			this.$emit("blur")
		}
	}

	clickAndFocus () {
		if (this.readonly) {
			this.focusWhenReady = true
			this.$emit("focus")
		}
	}

	private getEditorValue (): string {
		return this.editorContainer.children.length === 0
			? ""
			: this.editorContainer.children[0].innerHTML
	}

	private get minHeightStyleString (): string {
		return `min-height: ${this.minHeight};`
	}

	public getText (): string {
		return this.editor ? this.editor.getText() : ""
	}

	private handleEditorSelectionChange (range: RangeStatic, source: Sources) {
		// deactivate the link button before checking if it should be enabled
		this.isLinkButtonActive = false
		if (range == null) return
		if (source === "user") {
			// Find link element using range
			const [link, offset] = (this.editor!.scroll as any).descendant(
				Link,
				range.index
			)

			// activate the button if the selection is on a link element
			if (link && link.domNode && link.domNode.href) {
				this.isLinkButtonActive = true
			}
		}
	}

	private handleHyperlinkClick () {
		if (!this.editor!.hasFocus()) {
			this.editor!.focus()
		}
		this.showHyperlinkModal()
	}

	private showHyperlinkModal () {
		const range = this.editor!.getSelection()
		if (range == null) {
			// reset the local range variables
			this.editorRangeIndex = 0
			this.editorRangeLength = 0
			return
		}
		// set the variables with the range value so we can access them later
		this.editorRangeIndex = range.index
		this.editorRangeLength = range.length
		// check if the user has selected any text to apply a hyperlink to
		if (range.length > 0) {
			this.selectedText = this.editor!.getText(range.index, range.length)
		} else {
			this.selectedText = ""
		}

		// open the modal
		this.hyperlinkModalIsOpen = true
	}

	private handleHyperlinkModalSubmitClicked (hyperlinkObj: Hyperlink) {
		// delete the originally selected text as this will be replaced
		if (this.editorRangeLength > 0) {
			if (hyperlinkObj.replaceExistingText) {
				this.editor!.deleteText(this.editorRangeIndex, hyperlinkObj.text.length)
			}
		}

		// insert the hyperlink html
		this.editor!.insertText(this.editorRangeIndex, hyperlinkObj.text, "link", hyperlinkObj.url)

		// if the length of the editor selection is 0 (i.e., they have created new text at the same time as the hyperlink),
		// set the length to the length of the text they have created
		if (this.editorRangeLength === 0) {
			this.editorRangeLength = hyperlinkObj.text.length
		}

		// set the selection to the hyperlink text
		this.editor!.setSelection(this.editorRangeIndex, this.editorRangeLength, "api")

		this.hyperlinkModalIsOpen = false
	}

	private handleHyperlinkModalCancelClicked () {
		this.hyperlinkModalIsOpen = false
	}

	private addPlaceholder (placeholder: { id: string, label: string }) {
		const selection = this.editor!.getSelection(true)!

		const insertIndex = selection ? selection.index : this.editor!.getLength()
		this.editor!.insertEmbed(
			insertIndex,
			"placeholder",
			placeholder)

		this.editor!.insertText(insertIndex + 1, " ", "user")

		setTimeout(() => {
			this.editor!.setSelection(insertIndex + 2, 0, "silent")
			this.editor!.focus()
		}, 1)
	}

	insertImage (event: any) {
		let files = event && event.target ? (event.target.files || (event.dataTransfer ? event.dataTransfer.files : [])) as File[] : []

		if (!files) return

		let range = this.editor!.getSelection()

		if (range == null) {
			range = {
				index: 0,
				length: 0
			}

			// reset the local range variables
			this.editorRangeIndex = 0
			this.editorRangeLength = 0
		}

		try {
			files.forEach(file => {
				const reader = new FileReader()
				reader.onload = (event: any) => {
					const base64 = event.target.result
					this.editor!.clipboard.dangerouslyPasteHTML(range!.index, `<img src="${base64}" />`)
					this.$emit("blur")
				}

				reader.readAsDataURL(file)
			})
		} catch (ex) {
			console.log(ex)
		}
	}

	get richTextImagesEnabled (): boolean {
		return this.getIsFeatureEnabled(TenantFeatures.RichTextImages) && this.allowImages
	}
}
